import React, { Fragment, useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import Table from "react-bootstrap/Table";
import { PaginationControl } from "react-bootstrap-pagination-control";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import {
  AddDaysToDate,
  AxisDate,
  AxisDateTimeFull,
  AxisDateWithDay,
} from "../util/AxisDateTime";
import AxisSpinner from "../components/AxisSpinner";
import { UserLink } from "../util/Links";
import { Container } from "react-bootstrap";
import UserSelection from "../components/UserSelection";
import DateTimePicker from "react-datetime-picker";
import Button from "react-bootstrap/Button";
import { AvailabilityNewModal } from "../modals/AvailabilityNewModal";
import { BsCalendar2CheckFill } from "react-icons/bs";
import { DateTime } from "luxon";
import { AvailabilityEditModal } from "../modals/AvailabilityEditModal";
import AvailabilityUserNoteModal from "../modals/AvailabilityUserNoteModal";
import { PageTitleController } from "../components/PageTitleController";
import { SuccessCheckMark } from "../components/AxisCheckMark";
import { subDays } from "date-fns";

const AvailabilityTable = ({ selectedUser = null }) => {
  const historyKey = "AvailabilityTable";

  let history = localStorage.key("history")
    ? JSON.parse(localStorage.getItem("history"))
    : null;
  if (history !== null && historyKey in history)
    history = JSON.parse(history[historyKey]);
  else history = null;

  if (
    history !== null &&
    history?.lastUpdated !== undefined &&
    new Date() - new Date(history.lastUpdated) > 600000
  ) {
    localStorage.removeItem("history");
    history = null;
  }

  const UserDefaultValue = () => {
    if (selectedUser === null) return null;
    return selectedUser;
  };

  const { authAxios } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dataSource, setDataSource] = useState([]);
  const [offset, setOffset] = useState(history ? history?.offset : 0);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [fetchCount, setFetchCount] = useState(
    history ? history?.fetchCount : 25,
  );
  const [key, setKey] = useState(history ? history?.key : "all");
  const [user, setUser] = useState(
    history ? history?.user : UserDefaultValue(),
  );
  const [date, setDate] = useState(
    history?.date !== null && history?.date !== undefined
      ? new Date(history?.date)
      : null,
  );
  const [showAddAvailability, setShowAddAvailability] = useState(false);
  const [showAvailabilityNote, setShowAvailabilityNote] = useState(false);
  const [showEditAvailability, setShowEditAvailability] = useState(false);
  const [availabilityObject, setAvailabilityObject] = useState(null);
  const [pageHeaderMsg, setPageHeaderMsg] = useState(null);
  const [availabilityMaxDays, setAvailabilityMaxDays] = useState(null);
  const [availableRetentionDate, setAvailableRetentionDate] = useState(null);

  useEffect(() => {
    if (!selectedUser) document.title = "Availability";

    if (availableRetentionDate === null) {
      fetchMaxDayLengthFor(
        "/api/backoffice/availability/availability_retention_days",
      ).then((retentionDays) => {
        setAvailableRetentionDate(subDays(new Date(), retentionDays));
      });
    }
    if (availabilityMaxDays === null) {
      fetchMaxDayLengthFor(
        "/api/backoffice/availability/availability_max_days",
      ).then((maxDays) => {
        if (maxDays > 0) {
          setAvailabilityMaxDays(
            `App users can set their own availability up to ${AxisDateWithDay(AddDaysToDate(maxDays))} (${maxDays} days)`,
          );
        }
      });
    }
  }, []);

  useEffect(() => {
    setUser(selectedUser);
  }, [selectedUser]);

  const updateHistoryJSON = () => {
    if (selectedUser !== null) return;
    var saveStates = {};
    saveStates[historyKey] = JSON.stringify({
      key: key,
      offset: offset,
      user: user,
      date: date,
      lastUpdated: new Date(),
      fetchCount: fetchCount,
    });
    localStorage.setItem("history", JSON.stringify(saveStates));
  };

  useEffect(() => {
    paginate();
    updateHistoryJSON();
  }, [offset, key]);

  const forceUpdate = () => {
    if (offset === 0) paginate();
    setOffset(0);
    updateHistoryJSON();
  };

  const fetchMaxDayLengthFor = async (url) => {
    let days = 0;
    try {
      const response = await authAxios().get(url);
      days = response.data;
    } catch (e) {
      console.error(e);
    }
    return days;
  };

  const paginate = async () => {
    var params = {};
    if (key !== "all") {
      params["type"] = key;
    }

    if (user !== null) {
      params["user_id"] = user?.id;
    }

    if (date !== null) {
      params["date"] = DateTime.fromJSDate(date).toISODate();
    }

    try {
      setLoading(true);

      const pageSettings = {
        direction: "asc",
        key: "date",
        page: offset,
        elementPerPage: fetchCount,
      };
      const config = {
        headers: {
          "content-type": "application/json",
        },
        params: params,
      };

      const page = await authAxios().post(
        "/api/backoffice/availability/paging",
        pageSettings,
        config,
      );
      setLoading(false);

      if (page.data.content.length === 0) {
        if (dataSource.length === 0) {
          setTotal(0);
        }
        console.log("empty api call");
        setDataSource([]);
        return;
      }

      setDataSource(page.data.content);
      setTotal(page.data.totalElements);
      var length = Math.ceil(page.data.totalElements / fetchCount);
      setTotalPages(length);
    } catch (error) {
      setLoading(false);
      console.log("Error catch");
      console.log(error);
      console.log(error.stack);
    }
  };

  const closeEditAvailability = () => {
    setAvailabilityObject(null);
    setShowEditAvailability(false);
  };

  const SpinnerOrData = (loading) => {
    if (loading) return <AxisSpinner loading={loading} />;

    return (
      <Fragment>
        <Table striped bordered hover>
          <thead>
            <tr>
              <th>USER</th>
              <th>DATE</th>
              <th>EARLY</th>
              <th>LATE</th>
              <th>LONG DAY</th>
              <th>TWILIGHT</th>
              <th>NIGHT</th>
              <th>EVENING</th>
              <th>CREATED</th>
            </tr>
          </thead>
          <tbody>
            {dataSource.map((availability) => (
              <tr
                key={availability?.id}
                onClick={() => {
                  setAvailabilityObject(availability);
                  setShowEditAvailability(!showEditAvailability);
                }}
              >
                <td>{UserLink(availability?.user)}</td>
                <td>{AxisDate(availability?.date)}</td>
                <td>
                  {availability?.early === true ? SuccessCheckMark() : null}
                </td>
                <td>
                  {availability?.late === true ? SuccessCheckMark() : null}
                </td>
                <td>
                  {availability?.longday === true ? SuccessCheckMark() : null}
                </td>
                <td>
                  {availability?.twilight === true ? SuccessCheckMark() : null}
                </td>
                <td>
                  {availability?.night === true ? SuccessCheckMark() : null}
                </td>
                <td>
                  {availability?.evening === true ? SuccessCheckMark() : null}
                </td>
                <td>{AxisDateTimeFull(availability?.created)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
        <div>
          Page {offset + 1} of {totalPages}, showing {dataSource.length}{" "}
          records(s) out of {total}
          <div>
            <PaginationControl
              page={offset + 1}
              between={3}
              total={total}
              limit={fetchCount}
              changePage={(page) => {
                setOffset(page - 1);
              }}
              ellipsis={1}
            />
          </div>
        </div>
      </Fragment>
    );
  };

  const displayPageHeaderMsg = () => {
    if (pageHeaderMsg == null) return null;

    return <Row className={pageHeaderMsg.className}>{pageHeaderMsg.msg}</Row>;
  };

  const DisplayTabsAndFilters = () => {
    if (selectedUser !== null) return null;

    return (
      <Fragment>
        <Tabs
          id="shifts-table-tabs"
          activeKey={key}
          onSelect={(k) => setKey(k)}
          className="mb-3"
        >
          <Tab eventKey="all" title="All"></Tab>
          <Tab eventKey="EARLY" title="Early"></Tab>
          <Tab eventKey="LATE" title="Late"></Tab>
          <Tab eventKey="LONGDAY" title="Long Day"></Tab>
          <Tab eventKey="TWILIGHT" title="Twilight"></Tab>
          <Tab eventKey="NIGHT" title="Night"></Tab>
          <Tab eventKey="EVENING" title="Evening"></Tab>
        </Tabs>

        {availabilityMaxDays && (
          <div
            className="alert alert-success mb-3"
            role={"alert"}
            style={{ "--bs-alert-bg": "#d4edda" }}
          >
            {availabilityMaxDays}
          </div>
        )}

        <Container fluid className={"m-0 p-0"}>
          <Row xs="auto" className={"align-items-center g-3"}>
            <Col
              className={"btn-w-px fs-input axis-input"}
              as={DateTimePicker}
              format={"dd/MM/yyyy"}
              minDate={availableRetentionDate}
              onChange={(value) => {
                setDate(value);
              }}
              value={date}
            />
            <Col
              as={UserSelection}
              onSelected={setUser}
              defaultValue={user}
              placeholder="Select User"
            />
            <Col xs={"auto"}>
              <Button
                variant={"outline-offblack"}
                onClick={() => forceUpdate()}
              >
                Filter
              </Button>
            </Col>
          </Row>
        </Container>
      </Fragment>
    );
  };

  const DisplayPageTitle = () => {
    if (selectedUser !== null) return null;

    return (
      <PageTitleController
        title={"Availability"}
        icon={<BsCalendar2CheckFill className={"icon-right-spacing"} />}
      />
    );
  };

  return (
    <Fragment>
      {displayPageHeaderMsg()}
      <Row className={"icon-text-wrapper"}>
        {DisplayPageTitle()}
        <Col>
          <Container>
            <Row xs={"auto"} className={"justify-content-end g-2"}>
              {selectedUser && (
                <Col>
                  <Button
                    variant="outline-offblack"
                    onClick={() => setShowAvailabilityNote(true)}
                  >
                    Availability Note
                  </Button>
                </Col>
              )}
              <Col>
                <Button
                  variant="offblack"
                  onClick={() => setShowAddAvailability(true)}
                >
                  + New Availability
                </Button>
              </Col>
            </Row>
          </Container>
        </Col>
      </Row>

      {DisplayTabsAndFilters()}

      <br />
      {SpinnerOrData(loading)}

      <AvailabilityNewModal
        close={setShowAddAvailability}
        visible={showAddAvailability}
        forceUpdate={forceUpdate}
        user={selectedUser ? selectedUser : null}
      />
      <AvailabilityEditModal
        visible={showEditAvailability}
        availabilityObject={availabilityObject}
        pageHeaderMsg={pageHeaderMsg}
        close={setShowEditAvailability}
        forceUpdate={forceUpdate}
      />
      <AvailabilityUserNoteModal
        visible={showAvailabilityNote}
        close={() => setShowAvailabilityNote(false)}
        user={selectedUser}
        onUpdated={forceUpdate}
      />
    </Fragment>
  );
};

export default AvailabilityTable;
